"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

/* ─── Singleton loader ──────────────────────────────────────────────── */
let AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion | null = null
let loaderPromise: Promise<void> | null = null

function loadPlaces(): Promise<void> {
  if (loaderPromise) return loaderPromise
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY ?? ""
  if (!apiKey) return Promise.resolve()
  setOptions({ key: apiKey, v: "weekly" })
  loaderPromise = importLibrary("places")
    .then((lib) => {
      const places = lib as google.maps.PlacesLibrary
      AutocompleteSuggestion = places.AutocompleteSuggestion
    })
    .catch((err) => {
      console.error("Google Maps Places failed to load:", err)
      loaderPromise = null
    })
  return loaderPromise!
}

async function getPlaceDetails(
  pred: google.maps.places.PlacePrediction
): Promise<google.maps.places.Place | null> {
  try {
    const place = pred.toPlace()
    await place.fetchFields({ fields: ["addressComponents"] })
    return place
  } catch {
    return null
  }
}

function parsePlace(place: google.maps.places.Place): AddressResult {
  const components = place.addressComponents ?? []
  const get = (type: string) => components.find((c) => c.types.includes(type))?.longText ?? ""
  const streetNumber = get("street_number")
  const route = get("route")
  return {
    street: [streetNumber, route].filter(Boolean).join(" "),
    postalCode: get("postal_code"),
    city: get("locality") || get("administrative_area_level_2"),
  }
}

/* ─── Types ─────────────────────────────────────────────────────────── */
export interface AddressResult {
  street: string
  postalCode: string
  city: string
}

export interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  /** Called when user selects a suggestion — returns parsed street, postalCode, city */
  onSelect?: (result: AddressResult) => void
  placeholder?: string
  error?: boolean
  /** "default" = shadcn-like (admin dialogs), "inline" = matches page.tsx inputStyle */
  variant?: "default" | "inline"
  id?: string
  disabled?: boolean
}

/* ─── Dropdown via portal (échappe overflow:hidden des drawers/modals) ── */
interface DropdownPortalProps {
  anchorRef: React.RefObject<HTMLInputElement | null>
  children: React.ReactNode
}

function DropdownPortal({ anchorRef, children }: DropdownPortalProps) {
  const [rect, setRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    function update() {
      if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect())
    }
    update()
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [anchorRef])

  if (!rect) return null

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
        zIndex: 99999,
      }}
    >
      {children}
    </div>,
    document.body
  )
}

/* ─── Component ─────────────────────────────────────────────────────── */
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Rechercher une adresse…",
  error = false,
  variant = "default",
  id,
  disabled = false,
}: AddressAutocompleteProps) {
  const hasKey = !!(process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY ?? "")
  const [predictions, setPredictions] = useState<google.maps.places.AutocompleteSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [ready, setReady] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fetchIdRef = useRef(0)

  useEffect(() => {
    loadPlaces().then(() => setReady(true))
  }, [])

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [])

  const fetchPredictions = useCallback(async (input: string) => {
    if (!AutocompleteSuggestion || input.length < 3) {
      setPredictions([])
      setOpen(false)
      return
    }

    const fetchId = ++fetchIdRef.current

    try {
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        // subpremise : les adresses "bis/ter" (ex. 115 bis Rue de la Tour) sont
        // classées ainsi par Google et seraient exclues par street_address seul
        includedPrimaryTypes: ["street_address", "premise", "subpremise"],
        includedRegionCodes: ["fr"],
      })
      if (fetchId !== fetchIdRef.current) return
      const valid = suggestions.filter((s) => s.placePrediction != null)
      setPredictions(valid)
      setActive(-1)
      setOpen(valid.length > 0)
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return
      console.error("AutocompleteSuggestion error:", err)
      setPredictions([])
      setOpen(false)
    }
  }, [])

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    onChange(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchPredictions(val), 280)
  }

  async function handleSelect(suggestion: google.maps.places.AutocompleteSuggestion) {
    const pred = suggestion.placePrediction!
    setPredictions([])
    setOpen(false)
    setActive(-1)

    if (onSelect) {
      const place = await getPlaceDetails(pred)
      if (place) {
        const parsed = parsePlace(place)
        const street = parsed.street || pred.mainText?.text || pred.text.text
        onChange(street)
        onSelect({ ...parsed, street })
      } else {
        const street = pred.mainText?.text || pred.text.text
        onChange(street)
        onSelect({ street, postalCode: "", city: "" })
      }
    } else {
      onChange(pred.text.text)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open || predictions.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, predictions.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, -1))
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault()
      handleSelect(predictions[active])
    } else if (e.key === "Escape") {
      setOpen(false)
    }
  }

  const isOpen = open && predictions.length > 0

  const inputBase: React.CSSProperties = {
    width: "100%",
    fontFamily: "var(--font-sans), DM Sans, sans-serif",
    fontSize: 14,
    color: "#0F172A",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s, box-shadow 0.15s",
  }

  const inputVariant: React.CSSProperties =
    variant === "inline"
      ? {
          background: "#fff",
          border: `1px solid ${error ? "#EF4444" : isOpen ? "#0EA5E9" : "#E2E8F0"}`,
          borderRadius: 8,
          padding: "11px 14px",
          boxShadow: "none",
        }
      : {
          background: "#fff",
          border: `1px solid ${error ? "#EF4444" : isOpen ? "#0EA5E9" : "#E2E8F0"}`,
          borderRadius: 6,
          padding: "8px 12px",
          height: 36,
          boxShadow: isOpen ? "0 0 0 2px rgba(14,165,233,0.15)" : "none",
        }

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={hasKey && !ready ? "Chargement…" : placeholder}
        autoComplete="off"
        disabled={disabled}
        style={{ ...inputBase, ...inputVariant, opacity: disabled ? 0.6 : 1 }}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-controls="address-listbox"
      />

      {isOpen && (
        <DropdownPortal anchorRef={inputRef}>
          <div
            id="address-listbox"
            role="listbox"
            style={{
              background: "#fff",
              border: "1px solid #0EA5E9",
              borderRadius: "0 0 8px 8px",
              boxShadow: "0 12px 32px rgba(14,165,233,0.13), 0 4px 12px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {predictions.map((s, i) => {
              const pred = s.placePrediction!
              return (
                <button
                  key={pred.placeId}
                  role="option"
                  aria-selected={i === active}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelect(s)
                  }}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    padding: "10px 14px",
                    textAlign: "left",
                    background: i === active ? "#F0F9FF" : "transparent",
                    border: "none",
                    borderTop: i > 0 ? "1px solid #F1F5F9" : "none",
                    cursor: "pointer",
                    gap: 2,
                    transition: "background 0.1s",
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0F172A",
                      fontFamily: "var(--font-sans)",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 11, color: "#0EA5E9" }}>📍</span>
                    {pred.mainText?.text ?? pred.text.text}
                  </span>
                  {pred.secondaryText?.text && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "#94A3B8",
                        fontFamily: "var(--font-sans)",
                        paddingLeft: 17,
                      }}
                    >
                      {pred.secondaryText.text}
                    </span>
                  )}
                </button>
              )
            })}

            <div
              style={{
                padding: "5px 12px",
                background: "#F8FAFC",
                borderTop: "1px solid #F1F5F9",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  color: "#94A3B8",
                  fontFamily: "var(--font-sans)",
                  letterSpacing: 0.2,
                }}
              >
                Powered by Google
              </span>
            </div>
          </div>
        </DropdownPortal>
      )}
    </div>
  )
}
